/* eslint-disable jsx-a11y/no-autofocus -- the grid focuses a NEW row's first cell, in response to
   the teacher pressing Add row or Enter. That is focus following an action they just took, which is
   the case the rule exists to distinguish from stealing focus on load. */
import { soleVariableName } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronLeft, ChevronRight, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { collectXValues, isoDateInputToMs, msToIsoDateInput, parseDateValue, seriesDisplayLabel } from './dataviz-utils';
import { registerFieldReveal } from './field-reveal';
import { useRestoredData } from './use-restored-data';
import { useDelayedStickyError } from './use-settled-value';
import { VariableField } from './variable-field';


import type { TaskVariable } from '@/types/experience';
import type { ChartPoint, ChartSeries, DataVizBlockData } from '@agavi/blocks';

import Toast from '@/components/toast';
import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { DateField } from '@/components/ui/date-field';
import { IconButton } from '@/components/ui/icon-button';
import { NumberField } from '@/components/ui/number-field';
import SelectMenu from '@/components/ui/select-menu';
import { TextField } from '@/components/ui/text-field';
import { useTaskVariables } from '@/contexts/task-variables-context';

interface DataVizGridProps {
  data: DataVizBlockData;
  onChange: (next: DataVizBlockData) => void;
  /** Which series (if any) is two-click-armed for removal — lifted to the parent so the pinned
   * chart preview can highlight that whole series while its removal is armed, not just this
   * component's own trash icon. */
  armedSeriesId: string | null;
  onArmedSeriesIdChange: (id: string | null) => void;
  /** Series whose label collides with another's, from `issueFieldIds(issues)` in the parent — so
   *  the ring and the issue message read one derivation and cannot name different series. Only the
   *  ACTIVE series is on screen at a time (this is a cycler, not a list), so at most one rings; the
   *  message names the label, which is what points at the other member of the pair. */
  collidingSeriesIds?: ReadonlySet<string>;
}

type XType = 'number' | 'date' | 'text';

/** `xAxisType` only ever records 'date' or 'text' explicitly — a plain numeric column (or a fresh,
 * still-empty block) leaves it undefined. So "undefined" must NOT be read as "number" by default —
 * a CSV-imported category column (city names, product names, ...) also leaves xAxisType undefined,
 * and would otherwise show up mislabeled as "Number" here. Infer from the actual stored values
 * instead (any series with points, not just the first — a series added after the fact could be the
 * only one still empty), falling back to 'number' only when there's no data yet to inspect. */
function xTypeOf(data: DataVizBlockData): XType {
  if (data.xAxisType === 'date') return 'date';
  if (data.xAxisType === 'text') return 'text';
  const points = data.series.find((s) => s.points.length > 0)?.points ?? [];
  if (points.length === 0) return 'number';
  return points.every((p) => typeof p.x === 'number') ? 'number' : 'text';
}

/** One row per distinct x value, one y-text-cell per series — the x column is "locked": every
 * series shares the same set of rows, so switching the active series tab only ever swaps which y
 * cells are shown/edited, never re-shuffles or duplicates the x values underneath. */
interface GridRow {
  id: string;
  x: string;
  y: Record<string, string>;
}

function seriesToRows(series: ChartSeries[], xType: XType): GridRow[] {
  const hasAnyPoints = series.some((s) => s.points.length > 0);
  if (!hasAnyPoints) {
    return [{ id: crypto.randomUUID(), x: '', y: Object.fromEntries(series.map((s) => [s.id, ''])) }];
  }
  const xValues = collectXValues(series);
  const pointMaps = series.map((s) => new Map(s.points.map((p) => [p.x, p.y])));
  return xValues.map((x) => ({
    id: crypto.randomUUID(),
    x: xType === 'date' ? msToIsoDateInput(x as number) : String(x),
    y: Object.fromEntries(series.map((s, i) => [s.id, pointMaps[i]!.has(x) ? String(pointMaps[i]!.get(x)) : ''])),
  }));
}

function isNumericString(s: string): boolean {
  return s.trim() !== '' && !isNaN(Number(s));
}

/** Rebuilds every series' `points` from the shared rows — `seriesMetas` carries each series' own
 * id/label/color through untouched (only `points` is recomputed), so renaming a series or adding a
 * new one never has to separately patch existing series' data. When `xType === 'date'`, `r.x` holds
 * a native `<input type="date">` value (`yyyy-mm-dd`) parsed back to an epoch-ms timestamp — a row
 * whose `r.x` ISN'T a valid date-input value (see handleXTypeChange's non-destructive type switch)
 * is dropped from the chart rather than crashing, but its raw text stays intact in the grid for
 * switching back. When `xType === 'text'`, `r.x` is kept as a plain category label even if it looks
 * numeric — the teacher's explicit choice always wins over auto-detection. */
function rowsToSeries(rows: GridRow[], seriesMetas: ChartSeries[], xType: XType): ChartSeries[] {
  const xIsNumeric = xType === 'number' && rows.every((r) => r.x.trim() === '' || isNumericString(r.x));
  return seriesMetas.map((meta) => {
    const points = rows
      .map((r): ChartPoint | null => {
        const yRaw = r.y[meta.id] ?? '';
        if (r.x.trim() === '' || !yRaw.trim()) return null;
        /* A reference is stored as the text the teacher chose, resolved at the boundary when the
           chart is drawn. Everything else still has to be a number here. */
        const refName = soleVariableName(yRaw);
        const y: number | string = refName ? yRaw.trim() : Number(yRaw);
        if (typeof y === 'number' && isNaN(y)) return null;
        if (xType === 'date') {
          const x = isoDateInputToMs(r.x);
          return x === null ? null : { x, y };
        }
        if (xType === 'text') {
          return { x: r.x, y };
        }
        return { x: xIsNumeric ? Number(r.x) : r.x, y };
      })
      .filter((p): p is ChartPoint => p !== null);
    return { ...meta, points };
  });
}

interface GridRowFieldsProps {
  row: GridRow;
  rowCount: number;
  isLastRow: boolean;
  xType: XType;
  activeSeriesId: string;
  armedRowId: string | null;
  setArmedRowId: (id: string | null) => void;
  onXChange: (rowId: string, x: string) => void;
  onYChange: (rowId: string, y: string) => void;
  onRemoveRow: (rowId: string) => void;
  onEnterInLastRow: () => void;
  variables: TaskVariable[];
  autoFocus: boolean;
}

// The X/Y inputs and remove button — shared between the reorderable (sortable, grip handle) and
// static (no handle, one less grid column) row shells below, so the two never drift apart.
function GridRowFields({ row, rowCount, isLastRow, xType, activeSeriesId, armedRowId, setArmedRowId, onXChange, onYChange, onRemoveRow, onEnterInLastRow, variables, autoFocus }: GridRowFieldsProps) {
  const { t } = useTranslation();
  const isArmed = armedRowId === row.id;

  return (
    <>
      {xType === 'date' ? (
        <DateField
          value={row.x}
          onChange={(v) => onXChange(row.id, v)}
          ariaLabel={t('blocks.dataviz.grid.xColumnLabel')}
          className='min-w-0' />
      ) : xType === 'number' ? (
        <NumberField
          grammar='scientific'
          allowNegative
          autoFocus={autoFocus}
          value={row.x}
          onChange={(v) => onXChange(row.id, v)}
          ariaLabel={t('blocks.dataviz.grid.xColumnLabel')}
          className='min-w-0' />
      ) : (
        <TextField
          variant='prose'
          autoFocus={autoFocus}
          value={row.x}
          onChange={(v) => onXChange(row.id, v)}
          ariaLabel={t('blocks.dataviz.grid.xColumnLabel')}
          className='min-w-0' />
      )}

      {/* Y, unlike X, may name a variable. X is already `number | string` where a string means a
          CATEGORY LABEL, so a reference there would be ambiguous with a legitimate one. */}
      <VariableField
        value={row.y[activeSeriesId] ?? ''}
        onChange={(v) => onYChange(row.id, v)}
        variables={variables}
        // Enter, not Tab — Tab must always do its normal job (move focus to the next control,
        // e.g. the remove-row button, then out of the grid entirely) or a keyboard-only user could
        // never leave the grid once it has focus. Enter has no competing "move focus" meaning for a
        // plain text input, so repurposing it for "commit this row, give me a fresh one" is safe.
        onKeyDown={(e) => { if (isLastRow && e.key === 'Enter') { e.preventDefault(); onEnterInLastRow(); } }}
        ariaLabel={t('blocks.dataviz.grid.yColumnLabel')}
        className='min-w-0' />

      <IconButton
        type='button'
        variant='destructive'
        size='sm'
        isArmed={isArmed}
        disabled={rowCount <= 1}
        aria-label={isArmed ? t('blocks.dataviz.grid.confirmRemoveRow') : t('blocks.dataviz.grid.removeRow')}
        onClick={() => { if (isArmed) { onRemoveRow(row.id); setArmedRowId(null); } else { setArmedRowId(row.id); } }}
        onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedRowId(null); }}
        onBlur={() => { if (isArmed) setArmedRowId(null); }}
      >
        <Trash2 className='h-3.5 w-3.5' />
      </IconButton>
    </>
  );
}

// Reordering only ever changes anything when the x-axis is a category axis ('text') — for
// 'number'/'date', collectXValues always re-sorts by value regardless of row order in the grid, so
// a drag handle there would be a control that visibly does nothing. This variant carries the grip
// handle + dnd-kit wiring; StaticGridRow (below) is the same fields with neither, one grid column
// narrower, for the 'number'/'date' case.
function SortableGridRow(props: GridRowFieldsProps) {
  const { t } = useTranslation();
  const { row, armedRowId } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  const isArmed = armedRowId === row.id;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, gridTemplateColumns: '1.5rem 1fr 1fr 2.25rem' }}
      className={`grid items-center gap-2 ${isDragging ? 'opacity-40' : ''} ${isArmed ? `rounded-lg ${ARMED_DELETE_PAINT}` : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        // Through i18n like everything else. `aria-roledescription` is NOT translated by assistive
        // technology — whatever string is here is spoken verbatim — so a hardcoded English word was
        // read out to a Portuguese teacher exactly as written.
        aria-roledescription={t('blocks.dataviz.grid.sortableRole')}
        aria-label={t('blocks.dataviz.grid.dragRow')}
        className='flex h-7 w-4 shrink-0 cursor-grab touch-none items-center justify-center text-ws-muted hover:text-ws-text active:cursor-grabbing'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <GridRowFields {...props} />
    </div>
  );
}

function StaticGridRow(props: GridRowFieldsProps) {
  const { row, armedRowId } = props;
  const isArmed = armedRowId === row.id;

  return (
    <div
      style={{ gridTemplateColumns: '1fr 1fr 2.25rem' }}
      className={`grid items-center gap-2 ${isArmed ? `rounded-lg ${ARMED_DELETE_PAINT}` : ''}`}
    >
      <GridRowFields {...props} />
    </div>
  );
}

export function DataVizGrid({ data, onChange, armedSeriesId, onArmedSeriesIdChange, collidingSeriesIds }: DataVizGridProps) {
  const { t } = useTranslation();
  /* Read from context rather than taken as a prop: the grid is the only surface that AUTHORS a
     reference, and threading the list through the block purely to hand it back here would be one
     more prop for every caller to forget. */
  const variables = useTaskVariables();
  // Same rule as every other error marker: wait for the label to stop changing before the ring
  // first appears, then hold it until the collision is actually gone.

  const xType = xTypeOf(data);
  const [rows, setRows] = useState<GridRow[]>(() => seriesToRows(data.series, xType));
  /**
   * THE GRID HAS TO FOLLOW AN UNDO TOO — compared on the DATA, never on identity.
   *
   * `rows` is seeded once and then owned here, which was invisible while the only way to
   * replace the data wholesale was a CSV import — that remounts via `gridResetKey`. An undo
   * does not, so the chart rolled back and the grid went on showing the rows it had, and the
   * next keystroke committed those stale rows back over what was restored.
   *
   * The comparison is a ROUND TRIP, and the first attempt at this got it wrong in a way worth
   * recording, because both halves looked fine on their own:
   *
   *   - `seriesToRows` mints a fresh id per row, so `derive` can never deep-equal `current`
   *     while rows are the unit of comparison. Every echo looked like a change.
   *   - `rowsToSeries` drops a row whose x or y is still blank, which is every row mid-typing.
   *
   * Together: type "1" into x with y still empty, the commit writes `points: []`, the echo
   * comes back, the grid "restores" from the empty series, and the digit is erased under the
   * cursor. Reported as "I set up a simple data table, nothing is rendering, it says no data
   * yet" — with `points: []` in storage, because nothing ever survived long enough to save.
   *
   * So the question is not "is this object the one I sent" — the parent normalises, so it
   * never is — but "do my rows still produce the series I am being handed". A half-typed row
   * produces nothing and compares equal, which is exactly right: it means nobody else has
   * touched the data, so leave the teacher's cursor alone.
   */
  useRestoredData<{ series: ChartSeries[] }>({
    raw: data,
    // The grid only ever renders inside the open editor, so there is no second instance.
    isEditing: true,
    derive: (raw) => ({ series: (raw as DataVizBlockData).series }),
    current: { series: rowsToSeries(rows, data.series, xType) },
    apply: (next) => setRows(seriesToRows(next.series, xType)),
  });

  const [activeSeriesId, setActiveSeriesId] = useState<string>(() => data.series[0]?.id ?? crypto.randomUUID());
  const [armedRowId, setArmedRowId] = useState<string | null>(null);
  const [clearAllArmed, setClearAllArmed] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [justAddedRowId, setJustAddedRowId] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    if (!justAddedRowId) return;
    setJustAddedRowId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const activeSeries = data.series.find((s) => s.id === activeSeriesId) ?? data.series[0]!;
  // Same rule as every other error marker: wait for the label to stop changing before the ring
  // first appears, then hold it until the collision is actually gone.
  const showDuplicateLabel = useDelayedStickyError(
    !!collidingSeriesIds?.has(activeSeries.id), activeSeries.label);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function commit(nextRows: GridRow[]) {
    setRows(nextRows);
    /* No `markEmitted`: the parent normalises before the prop comes back, so the recorded
       string could never match, and an echo record that never matches is one that can only
       ever mark the WRONG thing as mine. The round-trip compare above is the mechanism. */
    onChange({ ...data, series: rowsToSeries(nextRows, data.series, xType) });
  }

  function handleXChange(rowId: string, x: string) {
    commit(rows.map((r) => (r.id === rowId ? { ...r, x } : r)));
  }

  function handleYChange(rowId: string, y: string) {
    commit(rows.map((r) => (r.id === rowId ? { ...r, y: { ...r.y, [activeSeriesId]: y } } : r)));
  }

  function handleAddRow(focusNew = false) {
    const newRow: GridRow = { id: crypto.randomUUID(), x: '', y: Object.fromEntries(data.series.map((s) => [s.id, ''])) };
    if (focusNew) setJustAddedRowId(newRow.id);
    commit([...rows, newRow]);
  }

  function handleRemoveRow(rowId: string) {
    if (rows.length <= 1) return;
    commit(rows.filter((r) => r.id !== rowId));
  }

  // A full reset, not just blanking points — collapses back to a single, freshly-named series
  // (mirroring a brand new block) rather than leaving whatever extra series/tabs the teacher had
  // built up, now all empty. "Clear all data" implies starting over, not "keep my series structure
  // but wipe the numbers."
  function handleClearAll() {
    const newSeriesId = crypto.randomUUID();
    setRows([{ id: crypto.randomUUID(), x: '', y: { [newSeriesId]: '' } }]);
    setActiveSeriesId(newSeriesId);
    setClearAllArmed(false);
    onChange({ ...data, series: [{ id: newSeriesId, label: '', points: [] }] });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((r) => r.id === active.id);
    const to = rows.findIndex((r) => r.id === over.id);
    if (from === -1 || to === -1) return;
    commit(arrayMove(rows, from, to));
    setAnnouncement(t('blocks.dataviz.grid.moveRowAnnouncement', { from: from + 1, to: to + 1 }));
  }

  // The whole point of this control: re-typing the x column must NEVER destroy data or force
  // starting over. Converting to/from 'date' reformats each row's value where it can (an ISO date
  // input <-> its epoch-ms number); a row that can't be reinterpreted (e.g. plain text that isn't a
  // real date) keeps its ORIGINAL raw value untouched rather than being cleared — it just won't
  // plot under the new type (rowsToSeries drops what it can't parse for the current type) until the
  // teacher fixes or removes it, and switching back to the previous type recovers it intact.
  function handleXTypeChange(nextType: XType) {
    let nextRows = rows;
    let unconvertedCount = 0;
    if (xType !== nextType && (xType === 'date' || nextType === 'date')) {
      nextRows = rows.map((r) => {
        if (!r.x.trim()) return r;
        if (xType === 'date') {
          const ms = isoDateInputToMs(r.x);
          if (ms === null) { unconvertedCount++; return r; }
          return { ...r, x: String(ms) };
        }
        const ms = parseDateValue(r.x);
        if (ms === null) { unconvertedCount++; return r; }
        return { ...r, x: msToIsoDateInput(ms) };
      });
      setRows(nextRows);
    }
    onChange({ ...data, series: rowsToSeries(nextRows, data.series, nextType), xAxisType: nextType === 'number' ? undefined : nextType });
    if (unconvertedCount > 0) {
      setToastMsg(t('blocks.dataviz.grid.xTypeSwitchWarning', { count: unconvertedCount }));
    }
  }

  function handleAddSeries() {
    const newId = crypto.randomUUID();
    const nextRows = rows.map((r) => ({ ...r, y: { ...r.y, [newId]: '' } }));
    const nextMetas = [...data.series, { id: newId, label: '', points: [] }];
    setRows(nextRows);
    setActiveSeriesId(newId);
    onChange({ ...data, series: rowsToSeries(nextRows, nextMetas, xType) });
  }

  function handleRemoveSeries(seriesId: string) {
    if (data.series.length <= 1) return;
    const nextRows = rows.map((r) => {
      const nextY = { ...r.y };
      delete nextY[seriesId];
      return { ...r, y: nextY };
    });
    const nextMetas = data.series.filter((s) => s.id !== seriesId);
    setRows(nextRows);
    if (activeSeriesId === seriesId) setActiveSeriesId(nextMetas[0]!.id);
    onArmedSeriesIdChange(null);
    onChange({ ...data, series: rowsToSeries(nextRows, nextMetas, xType) });
  }

  function handleRenameSeries(seriesId: string, label: string) {
    const nextMetas = data.series.map((s) => (s.id === seriesId ? { ...s, label } : s));
    onChange({ ...data, series: rowsToSeries(rows, nextMetas, xType) });
  }

  const hasAnyData = rows.some((r) => r.x.trim() !== '' || Object.values(r.y).some((v) => v.trim() !== ''));
  const seriesIds = data.series.map((s) => s.id).join('\u0000');
  useEffect(() => {
    const cleanups = seriesIds.split('\u0000').filter(Boolean).map(id => registerFieldReveal(id, () => setActiveSeriesId(id)));
    return () => cleanups.forEach(off => off());
  }, [seriesIds]);

  const activeIndex = data.series.findIndex((s) => s.id === activeSeries.id);
  const isLastSeries = activeIndex === data.series.length - 1;
  const isActiveSeriesArmed = armedSeriesId === activeSeries.id;
  // See SortableGridRow/StaticGridRow above — a drag handle only ever does anything for a category
  // x-axis; showing it for 'number'/'date' would just be dead weight costing horizontal space.
  const reorderable = xType === 'text';
  const headerGridTemplate = reorderable ? '1.5rem 1fr 1fr 2.25rem' : '1fr 1fr 2.25rem';

  return (
    <div className='flex flex-col gap-2'>
      <div aria-live='polite' aria-atomic='true' className='sr-only'>{announcement}</div>
      <div aria-live='assertive' aria-atomic='true' className='sr-only'>{clearAllArmed ? t('blocks.dataviz.grid.clearAllArmedAnnouncement') : ''}</div>

      {/* One merged header row, aligned to the same 4-column grid as every data row below: the x
          column keeps its type dropdown, and the y column becomes a series switcher — a name editor
          flanked by prev/next arrows, so there's exactly one place that's ever "open" at a time
          instead of a whole row of chips. The x column itself is shared/locked across every series
          (see GridRow), so switching series here only changes which y cells are shown/edited, never
          the rows themselves. Reaching the last series turns the next-arrow into an add-series
          button — there's nothing further to navigate to, so the same slot becomes "add" instead. */}
      <div
        className='grid items-center gap-2'
        style={{ gridTemplateColumns: headerGridTemplate }}
      >
        {reorderable && <div />}
        <div className='flex items-center justify-center gap-1.5 text-xs font-medium text-ws-muted'>
          <span>{t('blocks.dataviz.grid.xColumnLabel')}</span>
          <SelectMenu
            value={xType}
            onChange={v => handleXTypeChange(v as XType)}
            options={[
              { value: 'number', label: t('blocks.dataviz.grid.xType.number') },
              { value: 'date',   label: t('blocks.dataviz.grid.xType.date') },
              { value: 'text',   label: t('blocks.dataviz.grid.xType.text') },
            ]}
            ariaLabel={t('blocks.dataviz.grid.xTypeLabel')}
            className='w-fit' />
        </div>
        {/* Only this cell highlights when armed — removing a series never touches the shared x
            column/type, so the warning treatment shouldn't imply otherwise by spanning the row. */}
        <div data-field-id={activeSeries.id} className={`flex min-w-0 items-center gap-1 rounded-lg px-1 ${isActiveSeriesArmed ? `${ARMED_DELETE_PAINT}` : ''}`}>
          <IconButton
            type='button'
            size='sm'
            disabled={activeIndex <= 0}
            onClick={() => setActiveSeriesId(data.series[activeIndex - 1]!.id)}
            aria-label={t('blocks.dataviz.grid.previousSeries')}
            className='shrink-0'
          >
            <ChevronLeft className='h-3.5 w-3.5' />
          </IconButton>
          {/* Ring on a wrapper, as in keyword/fitb/dropdown: the panel names the duplicate label,
              the field says which series carries it. No `data-field-id` here — the row wrapper
              above already carries this series', and a nested duplicate of the same id would put
              two matches for one field in the DOM. */}
          <div className={`min-w-0 flex-1 rounded ${showDuplicateLabel ? 'ring-1 ring-ws-warning-fg' : ''}`}>
            <TextField fieldId={`${activeSeries.id}.label`}
              variant='prose'
              value={activeSeries.label}
              onChange={v => handleRenameSeries(activeSeries.id, v)}
              placeholder={seriesDisplayLabel({ ...activeSeries, label: '' }, activeIndex, data.series.length, data.yAxisLabel, t)}
              ariaLabel={t('blocks.dataviz.grid.seriesNameLabel')}
              className='w-full text-center font-medium' />
          </div>
          {isLastSeries ? (
            <IconButton
              type='button'
              size='sm'
              onClick={handleAddSeries}
              aria-label={t('blocks.dataviz.grid.addSeries')}
              title={t('blocks.dataviz.grid.addSeries')}
              className='shrink-0'
            >
              <Plus className='h-3.5 w-3.5' />
            </IconButton>
          ) : (
            <IconButton
              type='button'
              size='sm'
              onClick={() => setActiveSeriesId(data.series[activeIndex + 1]!.id)}
              aria-label={t('blocks.dataviz.grid.nextSeries')}
              className='shrink-0'
            >
              <ChevronRight className='h-3.5 w-3.5' />
            </IconButton>
          )}
        </div>
        <IconButton
          type='button'
          variant='destructive'
          size='sm'
          isArmed={armedSeriesId === activeSeries.id}
          disabled={data.series.length <= 1}
          aria-label={armedSeriesId === activeSeries.id ? t('blocks.dataviz.grid.confirmRemoveSeries') : t('blocks.dataviz.grid.removeSeries')}
          title={armedSeriesId === activeSeries.id ? t('blocks.dataviz.grid.confirmRemoveSeries') : t('blocks.dataviz.grid.removeSeries')}
          onClick={() => { if (armedSeriesId === activeSeries.id) { handleRemoveSeries(activeSeries.id); } else { onArmedSeriesIdChange(activeSeries.id); } }}
          onPointerLeave={(e) => { if (e.pointerType !== 'touch' && armedSeriesId === activeSeries.id) onArmedSeriesIdChange(null); }}
          onBlur={() => { if (armedSeriesId === activeSeries.id) onArmedSeriesIdChange(null); }}
        >
          <Trash2 className='h-3.5 w-3.5' />
        </IconButton>
      </div>

      {reorderable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            <div className='flex flex-col gap-1'>
              {rows.map((row, i) => (
                <SortableGridRow
                  key={row.id}
                  row={row}
                  rowCount={rows.length}
                  isLastRow={i === rows.length - 1}
                  xType={xType}
                  activeSeriesId={activeSeriesId}
                  armedRowId={armedRowId}
                  setArmedRowId={setArmedRowId}
                  onXChange={handleXChange}
                  onYChange={handleYChange}
                  onRemoveRow={handleRemoveRow}
                  onEnterInLastRow={() => handleAddRow(true)}
                  variables={variables}
                  autoFocus={row.id === justAddedRowId}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className='flex flex-col gap-1'>
          {rows.map((row, i) => (
            <StaticGridRow
              key={row.id}
              row={row}
              rowCount={rows.length}
              isLastRow={i === rows.length - 1}
              xType={xType}
              activeSeriesId={activeSeriesId}
              armedRowId={armedRowId}
              setArmedRowId={setArmedRowId}
              onXChange={handleXChange}
              onYChange={handleYChange}
              onRemoveRow={handleRemoveRow}
              onEnterInLastRow={() => handleAddRow(true)}
              variables={variables}
              autoFocus={row.id === justAddedRowId}
            />
          ))}
        </div>
      )}

      <AddRowButton
        label={t('blocks.dataviz.grid.addRow')}
        onClick={() => handleAddRow(false)}
      />
      <button
        type='button'
        disabled={!hasAnyData}
        onClick={() => { if (clearAllArmed) { handleClearAll(); } else { setClearAllArmed(true); } }}
        onPointerLeave={(e) => { if (e.pointerType !== 'touch' && clearAllArmed) setClearAllArmed(false); }}
        onBlur={() => { if (clearAllArmed) setClearAllArmed(false); }}
        aria-label={clearAllArmed ? t('blocks.dataviz.grid.confirmClearAll') : t('blocks.dataviz.grid.clearAll')}
        className={`flex w-full items-center justify-center gap-2 rounded-lg border py-3 text-sm font-medium disabled:opacity-40 ${
          clearAllArmed
            ? 'border-ws-warning-fg bg-ws-warning-bg/50 text-ws-warning-fg'
            : 'border-ws-border bg-transparent text-ws-text hover:border-ws-warning-fg hover:text-ws-warning-fg'
        }`}
      >
        <Trash2 className='h-4 w-4' />
        {clearAllArmed ? t('blocks.dataviz.grid.confirmClearAll') : t('blocks.dataviz.grid.clearAll')}
      </button>

      {toastMsg && <Toast message={toastMsg} type='warning' onDismiss={() => setToastMsg('')} />}
    </div>
  );
}
